import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
    attachWaitingActFile,
    dispatchShipmentRequest,
    downloadFileByUrl,
    downloadWaitingActTemplate,
    loadRequestDetail,
    resendWaitingActRequest,
} from './api';


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


const initialShipmentForm = {
    ttn: '',
    date_of_sending: '',
    pdfFile: null,
    isPickup: false,
};

const queueToastStorageKey = 'hyosung_queue_pending_toast';


export default function RequestDetailPage({
    title,
    backTo,
    backLabel,
    mode,
    fallbackDetails,
}) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [detailState, setDetailState] = useState({
        item: null,
        loading: true,
        error: '',
    });
    const [downloadState, setDownloadState] = useState({
        excel: false,
        pdf: false,
    });
    const [actsState, setActsState] = useState({
        file: null,
        loading: false,
        error: '',
    });
    const [shipmentState, setShipmentState] = useState({
        form: initialShipmentForm,
        loading: false,
        error: '',
    });
    const [toast, setToast] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const payload = await loadRequestDetail(slug);
                if (!cancelled) {
                    setDetailState({
                        item: payload,
                        loading: false,
                        error: '',
                    });
                    setShipmentState((current) => ({
                        ...current,
                        form: {
                            ...initialShipmentForm,
                            ttn: payload.ttn || '',
                        },
                    }));
                }
            } catch (error) {
                if (!cancelled) {
                    const fallback = fallbackDetails[slug] || null;
                    setDetailState({
                        item: fallback,
                        loading: false,
                        error: fallback ? '' : extractErrorMessage(error, 'Не удалось загрузить заявку.'),
                    });
                    setShipmentState((current) => ({
                        ...current,
                        form: {
                            ...initialShipmentForm,
                            ttn: fallback?.ttn || '',
                        },
                    }));
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [fallbackDetails, slug]);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setToast(null);
        }, 2600);

        return () => window.clearTimeout(timer);
    }, [toast]);

    const item = detailState.item;
    const moduleCounts = item?.module_counts?.by_system || {};
    const isCassetteRequest = item?.request_kind === 'cassettes';
    const totalUnits = item?.unit_count ?? item?.module_counts?.total ?? 0;

    const summaryCards = useMemo(() => [
        { label: 'Локация', value: item?.location?.city || '—' },
        { label: 'Получатель', value: item?.whom || item?.related || '—' },
        { label: isCassetteRequest ? 'Кассет' : 'Модулей', value: totalUnits },
        { label: 'План отгрузки', value: formatDate(item?.estimated_date_of_shipment) },
    ], [isCassetteRequest, item, totalUnits]);

    async function handleDownload(kind) {
        const fileUrl = kind === 'excel' ? item?.excel_file_url : item?.pdf_file_url;
        if (!fileUrl) {
            return;
        }

        setDownloadState((current) => ({
            ...current,
            [kind]: true,
        }));

        try {
            await downloadFileByUrl(fileUrl, `${item?.slug || 'request'}-${kind}`);
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось скачать файл.');
            setToast({ type: 'error', text: message });
        } finally {
            setDownloadState((current) => ({
                ...current,
                [kind]: false,
            }));
        }
    }

    async function handleDownloadTemplate() {
        try {
            await downloadWaitingActTemplate(slug);
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось скачать Excel-запрос.');
            setActsState((current) => ({
                ...current,
                error: message,
            }));
            setToast({ type: 'error', text: message });
        }
    }

    async function handleAttachAct(event) {
        event.preventDefault();
        if (!actsState.file) {
            setActsState((current) => ({
                ...current,
                error: 'Сначала выбери Excel акт.',
            }));
            return;
        }

        setActsState((current) => ({
            ...current,
            loading: true,
            error: '',
        }));

        const formData = new FormData();
        formData.append('excel_file', actsState.file);

        try {
            const response = await attachWaitingActFile(slug, formData);
            setActsState({
                file: null,
                loading: false,
                error: '',
            });
            setToast({ type: 'success', text: response.detail });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось прикрепить акт.');
            setActsState((current) => ({
                ...current,
                loading: false,
                error: message,
            }));
            setToast({ type: 'error', text: message });
        }
    }

    async function handleResendActRequest() {
        setActsState((current) => ({
            ...current,
            loading: true,
            error: '',
        }));

        try {
            const response = await resendWaitingActRequest(slug);
            setActsState((current) => ({
                ...current,
                loading: false,
                error: '',
            }));
            setToast({ type: 'success', text: response.detail });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось повторно отправить запрос акта.');
            setActsState((current) => ({
                ...current,
                loading: false,
                error: message,
            }));
            setToast({ type: 'error', text: message });
        }
    }

    async function handleDispatch(event) {
        event.preventDefault();

        setShipmentState((current) => ({
            ...current,
            loading: true,
            error: '',
        }));

        const formData = new FormData();
        formData.append('ttn', shipmentState.form.ttn);
        formData.append('date_of_sending', shipmentState.form.date_of_sending);
        if (shipmentState.form.pdfFile) {
            formData.append('pdf_file', shipmentState.form.pdfFile);
        }
        if (shipmentState.form.isPickup) {
            formData.append('is_pickup', '1');
        }

        try {
            const response = await dispatchShipmentRequest(slug, formData);
            setShipmentState({
                form: initialShipmentForm,
                loading: false,
                error: '',
            });
            window.sessionStorage.setItem(queueToastStorageKey, JSON.stringify({
                target: backTo,
                type: 'success',
                text: response.detail,
            }));
            navigate(backTo);
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось подтвердить отправку.');
            setShipmentState((current) => ({
                ...current,
                loading: false,
                error: message,
            }));
            setToast({ type: 'error', text: message });
        }
    }

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}

            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>{title}</h3>
                    </div>

                    <div className="shell-actionCluster">
                        <button
                            type="button"
                            className="shell-actionButton shell-actionButton--secondary"
                            onClick={() => navigate(backTo)}
                        >
                            {backLabel}
                        </button>
                    </div>
                </div>
            </section>

            {detailState.error ? <div className="shell-banner">{detailState.error}</div> : null}

            {detailState.loading ? (
                <section className="shell-section">
                    <div className="shell-emptyState shell-emptyState--compact">Загружаем заявку...</div>
                </section>
            ) : item ? (
                <>
                    <section className="shell-section">
                        <div className="shell-sectionHeader">
                            <div>
                                <h3>{item.application_name || item.slug}</h3>
                            </div>
                            <span>{item.slug}</span>
                        </div>

                        <div className="shell-pageSummary shell-pageSummary--queueTop">
                            {summaryCards.map((card) => (
                                <div key={card.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                    <span>{card.label}</span>
                                    <strong>{card.value}</strong>
                                </div>
                            ))}
                        </div>

                        <div className="shell-requestDetail shell-requestDetail--tableLayout">
                            <div className="shell-requestDetailGrid">
                                <div className="shell-requestDetailItem">
                                    <span>Получатель</span>
                                    <strong>{item.whom || item.related || '—'}</strong>
                                </div>
                                <div className="shell-requestDetailItem">
                                    <span>Транспорт</span>
                                    <strong>{item.transport || '—'}</strong>
                                </div>
                                <div className="shell-requestDetailItem">
                                    <span>Адрес</span>
                                    <strong>{item.address || '—'}</strong>
                                </div>
                                <div className="shell-requestDetailItem">
                                    <span>Дата заявки</span>
                                    <strong>{formatDate(item.application_sending)}</strong>
                                </div>
                                <div className="shell-requestDetailItem">
                                    <span>Дата отправки</span>
                                    <strong>{formatDate(item.date_of_sending)}</strong>
                                </div>
                                <div className="shell-requestDetailItem">
                                    <span>ТТН</span>
                                    <strong>{item.ttn || '—'}</strong>
                                </div>
                            </div>

                            <div className="shell-systemSummary">
                                <div className="shell-systemSummaryItem">
                                    <span>{isCassetteRequest ? 'Всего кассет' : 'Всего модулей'}</span>
                                    <strong>{totalUnits}</strong>
                                </div>
                                {Object.entries(moduleCounts).filter(([system, count]) => {
                                    if (isCassetteRequest) {
                                        return String(system).startsWith('CIT') && Number(count) > 0;
                                    }
                                    return !String(system).startsWith('CIT');
                                }).map(([system, count]) => (
                                    <div key={system} className="shell-systemSummaryItem">
                                        <span>{system}</span>
                                        <strong>{count}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {mode === 'acts' ? (
                        <section className="shell-section">
                            <div className="shell-sectionHeader">
                                <div>
                                    <h3>Действия по акту</h3>
                                </div>
                            </div>

                            <div className="shell-requestActionLayout shell-requestActionLayout--full">
                                <form className="shell-requestActionCard shell-requestActionForm shell-requestActionForm--actsUnified" onSubmit={handleAttachAct}>
                                    <span className="shell-cardLabel">Прикрепить акт</span>
                                    <div className="shell-requestActionTop shell-requestActionTop--acts">
                                        <button
                                            type="button"
                                            className="shell-actionButton shell-actionButton--secondary"
                                            onClick={handleDownloadTemplate}
                                            disabled={actsState.loading}
                                        >
                                            Скачать Excel-запрос
                                        </button>
                                        <button
                                            type="button"
                                            className="shell-actionButton shell-actionButton--secondary"
                                            onClick={handleResendActRequest}
                                            disabled={actsState.loading}
                                        >
                                            {actsState.loading ? 'Отправляем...' : 'Повторно запросить акт'}
                                        </button>
                                        {item.excel_file_url ? (
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--secondary"
                                                onClick={() => handleDownload('excel')}
                                                disabled={downloadState.excel}
                                            >
                                                {downloadState.excel ? 'Скачиваем...' : 'Скачать акт'}
                                            </button>
                                        ) : null}
                                    </div>
                                    <label className="shell-field shell-field--full shell-field--span2">
                                        <input
                                            type="file"
                                            accept=".xls,.xlsx"
                                            onChange={(event) => setActsState((current) => ({
                                                ...current,
                                                file: event.target.files?.[0] || null,
                                                error: '',
                                            }))}
                                        />
                                    </label>

                                    <div className="shell-searchActions shell-searchActions--flush">
                                        <button
                                            type="submit"
                                            className="shell-actionButton shell-actionButton--primary"
                                            disabled={actsState.loading}
                                        >
                                            {actsState.loading ? 'Сохраняем...' : 'Прикрепить акт'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {actsState.error ? <div className="shell-inlineMessage shell-inlineMessage--error">{actsState.error}</div> : null}
                        </section>
                    ) : null}

                    {mode === 'shipments' ? (
                        <section className="shell-section">
                            <div className="shell-sectionHeader">
                                <div>
                                    <h3>Подтверждение отправки</h3>
                                </div>
                            </div>

                            <div className="shell-requestActionLayout shell-requestActionLayout--full">
                                <form className="shell-requestActionCard shell-requestActionForm shell-requestActionForm--shipmentsUnified" onSubmit={handleDispatch}>
                                    <span className="shell-cardLabel">Данные отправки</span>
                                    <div className="shell-requestActionTop shell-requestActionTop--shipments">
                                        {item.excel_file_url ? (
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--secondary"
                                                onClick={() => handleDownload('excel')}
                                                disabled={downloadState.excel}
                                            >
                                                {downloadState.excel ? 'Скачиваем...' : 'Скачать акт'}
                                            </button>
                                        ) : null}
                                        {item.pdf_file_url ? (
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--secondary"
                                                onClick={() => handleDownload('pdf')}
                                                disabled={downloadState.pdf}
                                            >
                                                {downloadState.pdf ? 'Скачиваем...' : 'Скачать ТТН'}
                                            </button>
                                        ) : null}
                                    </div>
                                    <label className="shell-field">
                                        <span>TTN</span>
                                        <input
                                            value={shipmentState.form.ttn}
                                            onChange={(event) => setShipmentState((current) => ({
                                                ...current,
                                                form: {
                                                    ...current.form,
                                                    ttn: event.target.value,
                                                },
                                            }))}
                                            placeholder="Номер ТТН"
                                        />
                                    </label>
                                    <label className="shell-field">
                                        <span>Дата отправки</span>
                                        <input
                                            type="date"
                                            value={shipmentState.form.date_of_sending}
                                            onChange={(event) => setShipmentState((current) => ({
                                                ...current,
                                                form: {
                                                    ...current.form,
                                                    date_of_sending: event.target.value,
                                                },
                                            }))}
                                        />
                                    </label>
                                    <label className="shell-field shell-field--span2">
                                        <span>Файл ТТН</span>
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={(event) => setShipmentState((current) => ({
                                                ...current,
                                                form: {
                                                    ...current.form,
                                                    pdfFile: event.target.files?.[0] || null,
                                                },
                                            }))}
                                        />
                                    </label>
                                    <label className="shell-checkboxField shell-checkboxField--inline shell-field--full shell-field--span2">
                                        <input
                                            type="checkbox"
                                            checked={shipmentState.form.isPickup}
                                            onChange={(event) => setShipmentState((current) => ({
                                                ...current,
                                                form: {
                                                    ...current.form,
                                                    isPickup: event.target.checked,
                                                },
                                            }))}
                                        />
                                        <span>Самовывоз без PDF</span>
                                    </label>

                                    <div className="shell-searchActions shell-searchActions--flush">
                                        <button
                                            type="submit"
                                            className="shell-actionButton shell-actionButton--primary"
                                            disabled={shipmentState.loading}
                                        >
                                            {shipmentState.loading ? 'Сохраняем...' : 'Подтвердить отправку'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {shipmentState.error ? <div className="shell-inlineMessage shell-inlineMessage--error">{shipmentState.error}</div> : null}
                        </section>
                    ) : null}

                    <section className="shell-section">
                        <div className="shell-sectionHeader">
                            <div>
                                <h3>{isCassetteRequest ? 'Кассеты заявки' : 'Модули заявки'}</h3>
                            </div>
                            <span>{item.modules?.length ?? 0} шт.</span>
                        </div>

                        {(item.modules || []).length ? (
                            <div className="shell-tableWrap">
                                <table className="shell-table">
                                    <thead>
                                        <tr>
                                            <th>Серийный номер</th>
                                            <th>{isCassetteRequest ? 'Партия / кассета' : 'Партия / система'}</th>
                                            <th>Статус ремонта</th>
                                            <th>Статус движения</th>
                                            <th>Локация</th>
                                            <th>Инженер</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {item.modules.map((module) => (
                                            <tr key={module.id} className="shell-tableRow">
                                                <td>{module.serial_number}</td>
                                                <td>
                                                    {module.party_number || '—'}
                                                    <div className="shell-tableHint">{module.system || '—'}</div>
                                                </td>
                                                <td>{module.repair_status || '—'}</td>
                                                <td>{module.movement_status || '—'}</td>
                                                <td>{module.location?.city || '—'}</td>
                                                <td>{module.engineer || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="shell-emptyState shell-emptyState--compact">
                                {isCassetteRequest ? 'У заявки пока нет привязанных кассет.' : 'У заявки пока нет привязанных модулей.'}
                            </div>
                        )}
                    </section>
                </>
            ) : null}
        </div>
    );
}
